import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "@/App";
import {
  ArrowLeft,
  CreditCard,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Receipt,
  Calendar,
  DollarSign,
  Package,
  FileText,
  Loader,
  RefreshCw,
} from "lucide-react";
import toast from "react-hot-toast";

const PaymentHistoryPage = () => {
  const { user } = useContext(AuthContext);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/payments/history");
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error("Failed to fetch payment history:", error);
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      paid: {
        label: "Paid",
        color: "text-emerald-400",
        bg: "bg-emerald-500/20",
        icon: CheckCircle,
      },
      pending: {
        label: "Pending",
        color: "text-amber-400",
        bg: "bg-amber-500/20",
        icon: Clock,
      },
      expired: {
        label: "Expired",
        color: "text-red-400",
        bg: "bg-red-500/20",
        icon: XCircle,
      },
      failed: {
        label: "Failed",
        color: "text-red-400",
        bg: "bg-red-500/20",
        icon: XCircle,
      },
      refunded: {
        label: "Refunded",
        color: "text-blue-400",
        bg: "bg-blue-500/20",
        icon: RefreshCw,
      },
    };
    return configs[status] || configs.pending;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount, currency = "usd") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  };

  const generateReceipt = (transaction) => {
    // Create a printable receipt
    const receiptContent = `
      FINGERPRINTVS RECEIPT
      =====================
      
      Receipt #: ${transaction.id?.substring(0, 8).toUpperCase() || "N/A"}
      Date: ${formatDate(transaction.created_at)}
      
      Customer: ${user?.full_name || "N/A"}
      Email: ${transaction.user_email || user?.email || "N/A"}
      
      -----------------------------
      ITEM DETAILS
      -----------------------------
      Package: ${transaction.package_name || "N/A"}
      Tier: ${transaction.tier?.toUpperCase() || "N/A"}
      Billing: ${transaction.interval === "one-time" ? "One-time" : `${transaction.interval}ly`}
      
      -----------------------------
      PAYMENT DETAILS
      -----------------------------
      Amount: ${formatCurrency(transaction.amount, transaction.currency)}
      Status: ${transaction.payment_status?.toUpperCase() || "N/A"}
      Payment Method: Credit Card
      
      ${transaction.addon_names ? `Add-ons: ${transaction.addon_names.join(", ")}` : ""}
      
      -----------------------------
      
      Thank you for your business!
      support@fingerprintvs.com
      www.fingerprintvs.com
    `;

    // Create and download as text file
    const blob = new Blob([receiptContent], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `FingerprintVS_Receipt_${transaction.id?.substring(0, 8) || "receipt"}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success("Receipt downloaded!");
  };

  const TransactionDetailModal = ({ transaction, onClose }) => {
    if (!transaction) return null;
    const statusConfig = getStatusConfig(transaction.payment_status);
    const StatusIcon = statusConfig.icon;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-slate-800 border border-white/10 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Transaction Details</h3>
              <button
                onClick={onClose}
                className="text-white/60 hover:text-white transition"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/60">Status</span>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                <StatusIcon className="w-4 h-4" />
                {statusConfig.label}
              </span>
            </div>

            {/* Transaction ID */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/60">Transaction ID</span>
              <span className="text-sm text-white font-mono">
                {transaction.id?.substring(0, 12)}...
              </span>
            </div>

            {/* Date */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/60">Date</span>
              <span className="text-sm text-white">{formatDate(transaction.created_at)}</span>
            </div>

            {/* Divider */}
            <div className="border-t border-white/10" />

            {/* Package Info */}
            <div>
              <h4 className="text-sm font-medium text-white mb-3">Package Details</h4>
              <div className="bg-white/5 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Package</span>
                  <span className="text-sm text-white">{transaction.package_name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Tier</span>
                  <span className="text-sm text-white capitalize">{transaction.tier}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Billing Cycle</span>
                  <span className="text-sm text-white capitalize">
                    {transaction.interval === "one-time" ? "One-time" : transaction.interval}
                  </span>
                </div>
                {transaction.addon_names && transaction.addon_names.length > 0 && (
                  <div className="pt-2 border-t border-white/10">
                    <span className="text-sm text-white/60 block mb-2">Add-ons</span>
                    {transaction.addon_names.map((addon, idx) => (
                      <span key={idx} className="text-sm text-cyan-400 block">+ {addon}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Info */}
            <div>
              <h4 className="text-sm font-medium text-white mb-3">Payment Information</h4>
              <div className="bg-white/5 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Amount</span>
                  <span className="text-lg font-semibold text-white">
                    {formatCurrency(transaction.amount, transaction.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Currency</span>
                  <span className="text-sm text-white uppercase">{transaction.currency}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white/60">Email</span>
                  <span className="text-sm text-white">{transaction.user_email}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-white/10 flex gap-3">
            <button
              onClick={() => generateReceipt(transaction)}
              disabled={transaction.payment_status !== "paid"}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-cyan-500 text-white rounded-lg font-medium hover:bg-cyan-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Download Receipt
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2.5 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 relative" data-testid="payment-history-page">
      {/* Background */}
      <div
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      />
      <div className="fixed inset-0 z-0 bg-slate-900/90" />

      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="bg-white/10 backdrop-blur-md border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link
                  to="/dashboard"
                  className="flex items-center gap-2 text-white/70 hover:text-white transition"
                  data-testid="back-to-dashboard"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm">Dashboard</span>
                </Link>
                <div className="h-6 w-px bg-white/20" />
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-white">Payment History</h1>
                    <p className="text-xs text-white/60">View invoices and receipts</p>
                  </div>
                </div>
              </div>
              <button
                onClick={fetchPaymentHistory}
                className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white/70 rounded-lg hover:bg-white/20 hover:text-white transition text-sm"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Total Spent</p>
                  <p className="text-2xl font-bold text-white">
                    {formatCurrency(
                      transactions
                        .filter((t) => t.payment_status === "paid")
                        .reduce((sum, t) => sum + (t.amount || 0), 0)
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Transactions</p>
                  <p className="text-2xl font-bold text-white">{transactions.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Active Subscriptions</p>
                  <p className="text-2xl font-bold text-white">
                    {transactions.filter((t) => t.payment_status === "paid" && t.interval !== "one-time").length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Transactions Table */}
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl overflow-hidden">
            <div className="p-5 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">All Transactions</h2>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-16">
                <Receipt className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No transactions yet</h3>
                <p className="text-white/60 mb-6">Your payment history will appear here</p>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500 text-white rounded-lg font-medium hover:bg-cyan-400 transition"
                  data-testid="view-plans-btn"
                >
                  <Package className="w-4 h-4" />
                  View Plans
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="transactions-table">
                  <thead>
                    <tr className="bg-white/5">
                      <th className="px-5 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                        Package
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-white/60 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {transactions.map((transaction) => {
                      const statusConfig = getStatusConfig(transaction.payment_status);
                      const StatusIcon = statusConfig.icon;

                      return (
                        <tr
                          key={transaction.id}
                          className="hover:bg-white/5 transition cursor-pointer"
                          onClick={() => setSelectedTransaction(transaction)}
                          data-testid={`transaction-row-${transaction.id}`}
                        >
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-white/40" />
                              </div>
                              <div>
                                <p className="text-sm text-white">
                                  {new Date(transaction.created_at).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </p>
                                <p className="text-xs text-white/50">
                                  {new Date(transaction.created_at).toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <div>
                              <p className="text-sm text-white font-medium">
                                {transaction.package_name}
                              </p>
                              <p className="text-xs text-white/50 capitalize">
                                {transaction.tier} • {transaction.interval === "one-time" ? "One-time" : transaction.interval}
                              </p>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <p className="text-sm text-white font-semibold">
                              {formatCurrency(transaction.amount, transaction.currency)}
                            </p>
                          </td>
                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}
                            >
                              <StatusIcon className="w-3.5 h-3.5" />
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedTransaction(transaction);
                                }}
                                className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition"
                                title="View Details"
                                data-testid={`view-details-${transaction.id}`}
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              {transaction.payment_status === "paid" && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    generateReceipt(transaction);
                                  }}
                                  className="p-2 text-white/40 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition"
                                  title="Download Receipt"
                                  data-testid={`download-receipt-${transaction.id}`}
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="mt-8 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-white mb-1">Need help with a payment?</h3>
                <p className="text-sm text-white/60 mb-3">
                  If you have questions about a charge, need a refund, or require an official invoice,
                  please contact our support team.
                </p>
                <a
                  href="mailto:support@fingerprintvs.com"
                  className="text-sm text-cyan-400 hover:text-cyan-300 transition"
                >
                  support@fingerprintvs.com
                </a>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
};

export default PaymentHistoryPage;
